import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Edit3, Image, Plus, Search, Star, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { deleteProduct, getAdminProducts } from "../../lib/api";
import type { Product } from "../../types/content";
import { AdminHeader, ConfirmDialog, EmptyState, Notice } from "./AdminUi";

export function ProductList() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["admin-products"], queryFn: getAdminProducts });
  const [search, setSearch] = useState("");
  const [target, setTarget] = useState<Product | null>(null);
  const [notice, setNotice] = useState("");
  const mutation = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      setNotice("Product deleted.");
      setTarget(null);
      queryClient.invalidateQueries({ queryKey: ["admin-products"] });
      queryClient.invalidateQueries({ queryKey: ["public-landing"] });
    },
  });

  const products = useMemo(
    () =>
      (query.data || []).filter((product) =>
        `${product.name} ${product.category} ${product.platform}`.toLowerCase().includes(search.toLowerCase()),
      ),
    [query.data, search],
  );

  return (
    <>
      <AdminHeader
        title="Products"
        description="Manage product information, visibility, links, and image galleries."
        action={
          <Link className="button button-primary" to="/admin/products/new">
            New product <Plus size={17} />
          </Link>
        }
      />
      {notice && <Notice type="success" message={notice} onClose={() => setNotice("")} />}
      <section className="admin-card table-card">
        <div className="table-tools">
          <label className="search-box">
            <Search size={17} />
            <input placeholder="Search products" value={search} onChange={(event) => setSearch(event.target.value)} />
          </label>
          <span>{products.length} products</span>
        </div>
        {!products.length ? (
          <EmptyState
            title="No products found"
            description="Create a product or try a different search."
            action={<Link className="button button-primary" to="/admin/products/new">Create product</Link>}
          />
        ) : (
          <div className="table-scroll">
            <table className="admin-table">
              <thead>
                <tr><th>Product</th><th>Category</th><th>Platform</th><th>Images</th><th>Status</th><th>Order</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id}>
                    <td>
                      <div className="table-product">
                        <span>{product.name.slice(0, 1)}</span>
                        <div><strong>{product.name}</strong><small>/{product.slug}</small></div>
                        {product.isFeatured && <Star size={14} fill="currentColor" />}
                      </div>
                    </td>
                    <td>{product.category}</td>
                    <td>{product.platform}</td>
                    <td><span className="image-count"><Image size={14} /> {product.images.length}/3</span></td>
                    <td><i className={product.isActive ? "status-active" : "status-inactive"}>{product.isActive ? "Active" : "Inactive"}</i></td>
                    <td>{product.displayOrder}</td>
                    <td>
                      <div className="table-actions">
                        <Link to={`/admin/products/${product.id}/edit`} aria-label={`Edit ${product.name}`}><Edit3 size={16} /></Link>
                        <button onClick={() => setTarget(product)} aria-label={`Delete ${product.name}`}><Trash2 size={16} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      <ConfirmDialog
        open={Boolean(target)}
        title="Delete this product?"
        description={`${target?.name || "This product"} and its gallery will be removed from the CMS.`}
        onCancel={() => setTarget(null)}
        onConfirm={() => target && mutation.mutate(target.id)}
      />
    </>
  );
}
